/**
 * MissionDetailView -- Mission detail with linked tasks and instructions.
 * Uses event delegation on container (registered once) to avoid listener leaks.
 * Loads all data from list endpoints to minimize API calls.
 *
 * @module presentation/views/MissionDetailView
 */

import { MissionApiClient } from '../../infrastructure/api/MissionApiClient.js';
import { TaskApiClient } from '../../infrastructure/api/TaskApiClient.js';
import { InstructionApiClient } from '../../infrastructure/api/InstructionApiClient.js';
import { RewardService } from '../../application/RewardService.js';
import { leerCierre } from '../../application/misiones/cierreDeMision.js';
import { avisarCierre } from '../shared/avisoDeCierre.js';
import { camposPuertaHtml, engancharCamposPuerta } from '../shared/camposPuerta.js';
import { motivoPuerta } from '../../application/tareas/puertaTarea.js';
import { estaBloqueada, bloqueadaPor, etiquetaBloqueo, motivoTarea } from '../../application/tareas/bloqueoTarea.js';
import { renderTaskDetailHTML, bindTaskDetailEvents, normalizeTask, renderTagBadges } from '../shared/TaskDetailRenderer.js';
import { regresoHtml } from '../shared/regreso.js';

const STATUS_COLORS = {
  pending: '#d29922', in_progress: '#58a6ff',
  completed: '#238636', cancelled: '#6e7681',
  active: '#238636', archived: '#6e7681', draft: '#6e7681', por_cerrar: '#f0883e'
};

const STATUS_LABELS = {
  pending: 'pendiente', in_progress: 'en progreso',
  completed: 'completada', cancelled: 'cancelada'
};

const TASK_STATUSES = ['pending', 'in_progress', 'completed', 'cancelled'];
// por_cerrar (MISIONES-CANDADOS-T1): la pone la base al cerrar la ultima tarea; la cierra Ares con [VERIFICADO].
const MISSION_STATUSES = ['in_progress', 'active', 'por_cerrar', 'draft', 'completed', 'archived'];

const MISSION_STATUS_LABELS = {
  in_progress: 'en progreso', active: 'activa', por_cerrar: 'por cerrar', draft: 'borrador',
  completed: 'completada', archived: 'archivada'
};

let missionId = null;
let currentTaskIds = [];
let currentInstCodes = [];
let missionAbort = null;
let isRendering = false;
// SALV-PUERTA-API-T1: la tarea nueva de esta mision entra por la puerta con el codigo de la mision.
let puertaMision = null;
let linkedTaskData = [];

function badge(status) {
  const color = STATUS_COLORS[status] || '#6e7681';
  const label = STATUS_LABELS[status] || MISSION_STATUS_LABELS[status] || status;
  return `<span class="status-badge" style="background:${color}22;color:${color}">${label}</span>`;
}

function taskStatusOptions(current) {
  return TASK_STATUSES
    .filter(s => s !== current)
    .map(s => `<option value="${s}">${STATUS_LABELS[s]}</option>`)
    .join('');
}

export async function renderMissionDetail(container) {
  if (isRendering) return;
  isRendering = true;

  // Abortar listeners anteriores para evitar leak entre vistas
  if (missionAbort) missionAbort.abort();
  missionAbort = new AbortController();

  try {
    missionId = window.location.hash.split('/').pop();
    container.innerHTML = '<p class="empty-state">Cargando mision...</p>';

    const result = await MissionApiClient.getById(missionId);

    if (!result.ok) {
      container.innerHTML = `
        <section class="artifact-view">
          ${regresoHtml({ volverA: "#/missions", texto: "Misiones" })}
          <p class="empty-state">Error: ${result.error}</p>
        </section>`;
      return;
    }

    const m = result.data?.data || result.data;
    currentTaskIds = [...(m.taskIds || [])];
    currentInstCodes = [...(m.instructionCodes || [])];

    const [allTasks, allInstructions, allMissions] = await Promise.all([
      loadAllTasks(),
      loadAllInstructions(),
      loadAllMissions()
    ]);

    // Cargar detalles completos (steps + descripcion) para tareas vinculadas
    // Tareas eliminadas (404) se filtran silenciosamente y se limpian del array
    const linkedTasksFull = await Promise.all(
      currentTaskIds.map(async tid => {
        try {
          const res = await TaskApiClient.getById(tid);
          return res.ok ? (res.data?.data || res.data) : null;
        } catch { return null; }
      })
    );
    const linkedTasks = linkedTasksFull.filter(Boolean).map(normalizeTask);
    // Limpiar IDs huerfanos (tareas eliminadas) del array de la mision
    const validIds = linkedTasks.map(t => t.id);
    const hadOrphans = currentTaskIds.length !== validIds.length;
    if (hadOrphans) {
      currentTaskIds = currentTaskIds.filter(tid => validIds.some(v => String(v) === String(tid)));
      saveMissionLinks(); // async, no await - limpieza en background
    }
    linkedTaskData = linkedTasks;
    const TASK_STATUS_ORDER = { in_progress: 0, pending: 1, completed: 2, cancelled: 3 };
    linkedTaskData.sort((a, b) => (TASK_STATUS_ORDER[a.status] ?? 9) - (TASK_STATUS_ORDER[b.status] ?? 9));
    const linkedInsts = allInstructions.filter(i => currentInstCodes.includes(i.code));

    const completedTasks = linkedTasks.filter(t => t.status === 'completed').length;
    const totalTasks = linkedTasks.length;
    const pct = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;
    const missionDone = totalTasks > 0 && completedTasks === totalTasks;

    // Excluir tareas ya asignadas a OTRAS misiones del dropdown
    const otherMissionTaskIds = new Set();
    allMissions.forEach(mi => {
      if (mi.id !== missionId) (mi.taskIds || []).forEach(tid => otherMissionTaskIds.add(tid));
    });
    const availableTasks = allTasks.filter(t =>
      !currentTaskIds.includes(t.id) && !otherMissionTaskIds.has(t.id)
    );
    const availableInsts = allInstructions.filter(i => !currentInstCodes.includes(i.code));

    container.innerHTML = `
      <section class="artifact-view">
        ${regresoHtml({ volverA: "#/missions", texto: "Misiones" })}

        <div class="mission-header-card ${m.status === 'in_progress' ? 'mission-working' : ''}">
          <div class="mission-title-row">
            ${m.status === 'in_progress' ? '<span class="working-indicator"></span>' : ''}
            <code class="mission-code">${m.code || ''}</code>
            <h1>${m.title || ''}</h1>
            ${badge(m.status)}
            <select class="status-select" id="nx-mission-status" title="Cambiar estado de mision">
              <option value="" disabled selected>Cambiar estado</option>
              ${MISSION_STATUSES.filter(s => s !== m.status).map(s =>
                `<option value="${s}">${MISSION_STATUS_LABELS[s]}</option>`).join('')}
            </select>
          </div>
          <p class="mission-desc">${m.description || 'Sin descripcion'}</p>
          <div class="mission-meta">
            <span class="mission-agent-group" id="nx-mission-agents">
              ${[['ARGEMIRO-001','Argemiro'],['ARGYS-001','Argys'],['ARES-001','Ares']].map(([code,name]) =>
                `<label class="agent-check" style="display:inline-flex;align-items:center;gap:3px;margin-right:8px;font-size:0.875rem;cursor:pointer">
                  <input type="checkbox" data-agent-code="${code}" ${(m.description || '').includes(code) ? 'checked' : ''} style="accent-color:#238636">
                  ${name}
                </label>`
              ).join('')}
            </span>
            <span>Recompensa:
              <input type="number" id="nx-nxs-edit" class="nxs-inline-input" value="${m.rewardNexos || 0}" min="0" title="Editar recompensa">
              <strong>NXS</strong>
            </span>
            ${totalTasks > 0 ? `<span>Progreso: <strong>${completedTasks}/${totalTasks}</strong> tareas (${pct}%)</span>` : ''}
            ${missionDone ? '<span class="mission-complete-tag">MISION COMPLETADA</span>' : ''}
          </div>
          ${totalTasks > 0 ? `<div class="progress-bar progress-bar-lg"><div class="progress-fill" style="width:${pct}%;background:${missionDone ? '#238636' : '#58a6ff'}"></div></div>` : ''}
        </div>

        <div class="detail-section">
          <div class="section-header">
            <h2>Tareas vinculadas (${totalTasks})</h2>
          </div>
          <div class="link-form">
            <select id="nx-task-select" class="form-input form-input-sm">
              <option value="">+ Vincular tarea...</option>
              ${availableTasks.map(t => `<option value="${t.id}">${t.title} (${t.status})</option>`).join('')}
            </select>
          </div>
          <div class="link-form" style="margin-top:6px">
            <form id="nx-mission-new-task" style="display:flex;gap:6px;flex-wrap:wrap;align-items:flex-start">
              <input type="text" id="nx-mt-title" placeholder="Nueva tarea..." class="form-input form-input-sm" style="flex:1;min-width:180px" required>
              <input type="text" id="nx-mt-desc" placeholder="Descripcion (opcional)" class="form-input form-input-sm" style="flex-basis:100%">
              ${camposPuertaHtml('nx-mt-puerta', { conMision: false })}
              <button type="submit" class="btn-primary btn-sm" style="white-space:nowrap">+ Crear y vincular</button>
            </form>
          </div>
          <div class="artifact-list" id="nx-mission-tasks">
            ${renderTaskRows(linkedTasks)}
          </div>
        </div>

        <div class="detail-section">
          <div class="section-header">
            <h2>Instrucciones aplicables (${linkedInsts.length})</h2>
          </div>
          <div class="link-form">
            <select id="nx-inst-select" class="form-input form-input-sm">
              <option value="">+ Vincular instruccion...</option>
              ${availableInsts.map(i => `<option value="${i.code}">[${i.code}] ${i.title}</option>`).join('')}
            </select>
          </div>
          <div class="artifact-list" id="nx-mission-insts">
            ${renderInstRows(linkedInsts)}
          </div>
        </div>

        <p id="nx-link-status" class="link-status" hidden></p>
      </section>`;

    bindDelegation(container, missionAbort.signal);
    bindAgentCheckboxes(m, missionId);
    const $nuevaTarea = container.querySelector('#nx-mission-new-task');
    puertaMision = $nuevaTarea
      ? engancharCamposPuerta($nuevaTarea, 'nx-mt-puerta', { $titulo: container.querySelector('#nx-mt-title'), misionFija: m.code || null })
      : null;

    // Cleanup al navegar: aborta todos los listeners de esta vista
    container._cleanupBusinessListener = () => {
      if (missionAbort) { missionAbort.abort(); missionAbort = null; }
    };
  } finally {
    isRendering = false;
  }
}

function renderTaskRows(tasks) {
  if (!tasks.length) return '<p class="empty-state">Sin tareas vinculadas</p>';
  return tasks.map(t => {
    const tmpl = (t.tags || []).includes('template');
    const desc = t.description || '';
    const descPreview = desc.length > 120
      ? desc.slice(0, 120).replace(/</g, '&lt;') + '\u2026'
      : desc.replace(/</g, '&lt;');

    // Expand section uses shared TaskDetailRenderer (single source of truth)
    const expandedHtml = `
      <div id="nx-task-detail-${t.id}" hidden style="padding:10px 0 4px;border-top:1px solid #21262d;margin-top:8px;width:100%">
        ${renderTaskDetailHTML(t)}
      </div>`;

    return `
    <div class="artifact-item linked-item${tmpl ? ' task-row-template' : ''}" data-task-id="${t.id}" style="display:flex;flex-direction:column;align-items:stretch">
      <div style="display:flex;justify-content:space-between;align-items:center;width:100%">
        <div class="task-info" style="flex:1;min-width:0;cursor:pointer;overflow:hidden" data-toggle-task="${t.id}">
          <div class="artifact-item-title" style="overflow:hidden;text-overflow:ellipsis;white-space:nowrap;max-width:100%">
            ${t.title}
            ${renderTagBadges(t.tags)}
            <span style="font-size:0.875rem;color:#6e7681;margin-left:4px">\u25B8</span>
          </div>
          ${descPreview ? `<div style="font-size:0.875rem;color:#8b949e;margin-top:2px;word-break:break-word">${descPreview}</div>` : ''}
          <div class="artifact-item-meta">${t.project || t.projectId || 'General'}</div>
        </div>
        <div class="task-actions" style="flex-shrink:0;display:flex;align-items:center;gap:4px">
          ${tmpl
            ? `<span class="status-badge" style="background:#d2992222;color:#d29922;font-size:0.875rem">plantilla</span>`
            : `${estaBloqueada(t) ? `<span class="status-badge" data-bloqueada style="background:#f8514922;color:#f85149" title="Espera a ${bloqueadaPor(t).join(', ')}: se libera sola cuando se cierren o salga su VIVO">&#128274; ${etiquetaBloqueo(t)}</span>` : badge(t.status)}
              <select class="status-select" data-task-status-id="${t.id}" title="Cambiar estado">
                <option value="" disabled selected>Cambiar</option>
                ${taskStatusOptions(t.status)}
              </select>`}
          <button class="btn-icon btn-unlink" data-unlink-task="${t.id}" title="Desvincular">&times;</button>
        </div>
      </div>
      ${expandedHtml}
    </div>`;
  }).join('');
}

function renderInstRows(instructions) {
  if (!instructions.length) return '<p class="empty-state">Sin instrucciones vinculadas</p>';
  return instructions.map(i => `
    <div class="artifact-item linked-item">
      <div class="task-info">
        <div class="artifact-item-title"><code class="instruction-code">${i.code}</code> ${i.title}</div>
        <div class="artifact-item-meta">${i.body?.substring(0, 80)?.replace(/\n/g, ' ') || ''}</div>
      </div>
      <div class="task-actions">
        ${badge(i.status)}
        <button class="btn-icon btn-unlink" data-unlink-inst="${i.code}" title="Desvincular">&times;</button>
      </div>
    </div>`).join('');
}

function bindAgentCheckboxes(m, missionId) {
  const group = document.getElementById('nx-mission-agents');
  if (!group) return;
  let saveTimer;
  group.addEventListener('change', () => {
    clearTimeout(saveTimer);
    saveTimer = setTimeout(async () => {
      const cbs = group.querySelectorAll('[data-agent-code]');
      const selected = [...cbs].filter(cb => cb.checked).map(cb => cb.dataset.agentCode);
      const cleanDesc = (m.description || '').replace(/\[AGENTE:\s*[^\]]*\]\s*/gi, '').trim();
      const tag = selected.length > 0 ? `[AGENTE: ${selected.join(', ')}]` : '';
      const newDesc = tag ? `${tag} ${cleanDesc}` : cleanDesc;
      const result = await MissionApiClient.update(missionId, { description: newDesc });
      if (result.ok) {
        m.description = newDesc;
        showStatus(`Agente → ${selected.join(', ') || 'sin asignar'}`);
      } else {
        showStatus(`Error: ${result.error}`, true);
      }
    }, 600);
  });
}

function showStatus(msg, isError = false) {
  const $status = document.getElementById('nx-link-status');
  if (!$status) return;
  $status.textContent = msg;
  $status.hidden = false;
  $status.style.color = isError ? '#f85149' : '#238636';
  setTimeout(() => { $status.hidden = true; }, 3000);
}

function bindDelegation(container, signal) {
  // --- Shared module handles: title edit, desc save, step edit/delete/add/checkbox ---
  bindTaskDetailEvents(container, {
    getTask(taskId) {
      return linkedTaskData.find(t => t.id === taskId || String(t.id) === String(taskId));
    },
    onSaved(_taskId, _field, _value) {
      showStatus(_field === 'title' ? 'Titulo actualizado' : 'Descripcion guardada');
    },
    onStepChanged(_taskId) {
      showStatus('Paso actualizado');
    },
    onReload(_taskId) {
      renderMissionDetail(container);
    }
  }, { signal });

  // --- Mission-specific change handlers (status, NXS, links, task status) ---
  container.addEventListener('change', async (e) => {
    if (signal?.aborted) return;
    if (isRendering) return;

    // Skip step checkboxes (handled by shared module)
    if (e.target.dataset.stepTask && e.target.type === 'checkbox') return;

    const missionStatus = e.target.id === 'nx-mission-status' ? e.target : null;
    if (missionStatus) {
      const newStatus = missionStatus.value;
      if (!newStatus) return;
      missionStatus.disabled = true;
      const result = await MissionApiClient.update(missionId, { status: newStatus });
      if (result.ok) {
        showStatus(`Mision → ${MISSION_STATUS_LABELS[newStatus]}`);
        await renderMissionDetail(container);
      } else {
        // Cerrar a mano tambien paga (PR 435): un no admin recibe 403 SOLO_ADMIN_CIERRA y se dice con la misma frase.
        showStatus(newStatus === 'completed' ? leerCierre(result).frase : `Error: ${result.error}`, true);
        missionStatus.disabled = false;
      }
      return;
    }

    // Agent checkboxes handled by bindAgentCheckboxes() directly on #nx-mission-agents

    const nxsEdit = e.target.id === 'nx-nxs-edit' ? e.target : null;
    if (nxsEdit) {
      const val = parseInt(nxsEdit.value, 10) || 0;
      const result = await MissionApiClient.update(missionId, { rewardNexos: val });
      if (result.ok) showStatus(`Recompensa → ${val} NXS`);
      else showStatus(`Error: ${result.error}`, true);
      return;
    }

    const taskSelect = e.target.id === 'nx-task-select' ? e.target : null;
    if (taskSelect) {
      const taskId = taskSelect.value;
      if (!taskId) return;
      currentTaskIds.push(taskId);
      await saveMissionLinks();
      await renderMissionDetail(container);
      return;
    }

    const instSelect = e.target.id === 'nx-inst-select' ? e.target : null;
    if (instSelect) {
      const code = instSelect.value;
      if (!code) return;
      currentInstCodes.push(code);
      await saveMissionLinks();
      await renderMissionDetail(container);
      return;
    }

    const taskStatusSelect = e.target.closest('[data-task-status-id]');
    if (taskStatusSelect) {
      const taskId = taskStatusSelect.dataset.taskStatusId;
      const newStatus = taskStatusSelect.value;
      if (!taskId || !newStatus) return;
      taskStatusSelect.disabled = true;
      const result = await TaskApiClient.updateStatus(taskId, newStatus);
      if (result.ok) {
        showStatus(`Tarea → ${STATUS_LABELS[newStatus]}`);
        if (newStatus === 'completed') {
          const { completadas: completed, rechazadas } = await RewardService.checkMissionCompletion(taskId);
          // SEC quinta puerta (PR 435): si la mision no se pudo cerrar (no admin), se dice; antes el fallo se tragaba.
          if (rechazadas.length > 0) avisarCierre(rechazadas[0].frase, rechazadas[0].motivo);
          if (completed.length > 0) {
            const names = completed.map(m => m.title || m.code).join(', ');
            const nxs = completed.reduce((s, m) => s + (m.rewardNexos || 0), 0);
            // Floating toast (survives DOM rebuild)
            const toast = document.createElement('div');
            toast.className = 'reward-toast';
            toast.innerHTML = `<strong>Mision completada!</strong><br>${names}<br><span class="nxs-earned">+${nxs} NXS</span>`;
            document.body.appendChild(toast);
            requestAnimationFrame(() => toast.classList.add('visible'));
            setTimeout(() => { toast.classList.remove('visible'); setTimeout(() => toast.remove(), 400); }, 5000);
          }
        } else {
          const reverted = await RewardService.checkMissionRevert(taskId);
          if (reverted.length > 0) {
            const names = reverted.map(m => m.title || m.code).join(', ');
            const nxs = reverted.reduce((s, m) => s + (m.rewardNexos || 0), 0);
            const toast = document.createElement('div');
            toast.className = 'reward-toast';
            toast.style.borderColor = '#f85149';
            toast.innerHTML = `<strong>Mision revertida</strong><br>${names}<br><span class="nxs-earned" style="color:#f85149">-${nxs} NXS</span>`;
            document.body.appendChild(toast);
            requestAnimationFrame(() => toast.classList.add('visible'));
            setTimeout(() => { toast.classList.remove('visible'); setTimeout(() => toast.remove(), 400); }, 5000);
          }
        }
        await renderMissionDetail(container);
      } else {
        // SALV-DEPENDENCIAS-T1: una bloqueada (409 TAREA_BLOQUEADA) se dice con lo que espera.
        showStatus(motivoTarea(result), true);
        taskStatusSelect.value = '';
        taskStatusSelect.disabled = false;
      }
      return;
    }
  });

  // --- Mission-specific click handlers (toggle, unlink) ---
  // Note: step delete, add step, save desc, title edit are ALL handled by shared module above
  container.addEventListener('click', async (e) => {
    if (signal?.aborted || isRendering) return;

    // Toggle expandir/colapsar detalle de tarea
    const toggleTask = e.target.closest('[data-toggle-task]');
    if (toggleTask) {
      const taskId = toggleTask.dataset.toggleTask;
      const detail = document.getElementById(`nx-task-detail-${taskId}`);
      if (detail) detail.hidden = !detail.hidden;
      return;
    }

    const unlinkTask = e.target.closest('[data-unlink-task]');
    if (unlinkTask) {
      const id = unlinkTask.dataset.unlinkTask;
      currentTaskIds = currentTaskIds.filter(tid => tid !== id);
      await saveMissionLinks();
      await renderMissionDetail(container);
      return;
    }

    const unlinkInst = e.target.closest('[data-unlink-inst]');
    if (unlinkInst) {
      const code = unlinkInst.dataset.unlinkInst;
      currentInstCodes = currentInstCodes.filter(c => c !== code);
      await saveMissionLinks();
      await renderMissionDetail(container);
      return;
    }
  });

  container.addEventListener('submit', async (e) => {
    if (signal?.aborted) return;
    if (e.target.id !== 'nx-mission-new-task') return;
    e.preventDefault();
    if (isRendering) return;

    const title = document.getElementById('nx-mt-title')?.value?.trim();
    const description = document.getElementById('nx-mt-desc')?.value?.trim();
    if (!title) return;

    const p = puertaMision ? puertaMision.leer() : { ok: false, error: 'La mision no tiene codigo: no se puede crear la tarea por la puerta' };
    if (!p.ok) { showStatus(p.error, true); return; }

    const btn = e.target.querySelector('button[type="submit"]');
    if (btn) btn.disabled = true;

    const createRes = await TaskApiClient.create({
      title,
      description: description || undefined,
      project: 'panel-v2',
      status: 'pending',
      priority: 'medium',
      ...p.campos
    });

    if (createRes.ok) {
      const newTask = createRes.data?.data || createRes.data;
      if (newTask?.id) {
        currentTaskIds.push(newTask.id);
        await saveMissionLinks();
      }
      showStatus('Tarea creada y vinculada');
      await renderMissionDetail(container);
    } else {
      showStatus(motivoPuerta(createRes), true);
      if (btn) btn.disabled = false;
    }
  });
}

async function saveMissionLinks() {
  const result = await MissionApiClient.update(missionId, {
    taskIds: currentTaskIds,
    instructionCodes: currentInstCodes
  });
  if (!result.ok) {
    showStatus(`Error al guardar: ${result.error}`, true);
  }
}

async function loadAllTasks() {
  const result = await TaskApiClient.list(1, 100);
  if (!result.ok) return [];
  return result.data?.data?.items || result.data?.items || [];
}

async function loadAllInstructions() {
  const result = await InstructionApiClient.list(1, 100);
  if (!result.ok) return [];
  return result.data?.data?.items || result.data?.items || [];
}

async function loadAllMissions() {
  const result = await MissionApiClient.list(1, 200);
  if (!result.ok) return [];
  return result.data?.data?.items || result.data?.items || [];
}
